import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { mediaApi } from '@/lib/api';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Code,
  Minus,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const MenuButton = ({ onClick, isActive, children, title }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    className={cn(
      "h-8 w-8 p-0",
      isActive && "bg-muted text-foreground"
    )}
    title={title}
  >
    {children}
  </Button>
);

const MenuBar = ({ editor }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  if (!editor) return null;

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkUrl('');
    }
  };

  const addImage = () => {
    if (!imageUrl) return;
    if (!altText.trim()) {
      setUploadError('Lütfen görsel için bir alt metin girin.');
      return;
    }

    editor
      .chain()
      .focus()
      .setImage({ src: imageUrl, alt: altText.trim() })
      .run();

    setImageUrl('');
    setAltText('');
    setUploadError('');
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!altText.trim()) {
      setUploadError('Lütfen görsel için bir alt metin girin.');
      event.target.value = '';
      return;
    }

    setUploadError('');
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await mediaApi.uploadImage(file, altText.trim());
      const { url, width, height, alt } = res;
      if (url) {
        editor
          .chain()
          .focus()
          .setImage({ src: url, alt: alt || altText.trim(), width, height })
          .run();
        toast.success('Görsel içeriğe eklendi');
        setAltText('');
        setImageUrl('');
      } else {
        toast.error('Yüklenen görsel için URL alınamadı');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      const message = error.response?.data?.detail || 'Görsel yüklenirken bir hata oluştu';
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted/30">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Kalın"
      >
        <Bold className="h-4 w-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="İtalik"
      >
        <Italic className="h-4 w-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Altı Çizili"
      >
        <UnderlineIcon className="h-4 w-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Üstü Çizili"
      >
        <Strikethrough className="h-4 w-4" />
      </MenuButton>

      <div className="w-px h-6 bg-border mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Başlık 2"
      >
        <Heading2 className="h-4 w-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        title="Başlık 3"
      >
        <Heading3 className="h-4 w-4" />
      </MenuButton>

      <div className="w-px h-6 bg-border mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Madde İşaretli Liste"
      >
        <List className="h-4 w-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numaralı Liste"
      >
        <ListOrdered className="h-4 w-4" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Alıntı"
      >
        <Quote className="h-4 w-4" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Kod Bloğu"
      >
        <Code className="h-4 w-4" />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Yatay Çizgi"
      >
        <Minus className="h-4 w-4" />
      </MenuButton>

      <div className="w-px h-6 bg-border mx-1" />

      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn("h-8 w-8 p-0", editor.isActive('link') && "bg-muted")}
            title="Link Ekle"
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-2">
            <Input
              placeholder="https://..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addLink()}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={addLink}>Ekle</Button>
              {editor.isActive('link') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => editor.chain().focus().unsetLink().run()}
                >
                  Kaldır
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            title="Görsel Ekle"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 space-y-4">
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Alt Metin (zorunlu)</p>
            <Input
              placeholder="Örn: Sol kanatta topla ilerleyen oyuncu"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
            />
          </div>
          {uploadError && (
            <p className="text-xs text-destructive">{uploadError}</p>
          )}
          <div className="space-y-2 pt-1">
            <p className="text-xs font-medium text-muted-foreground">Bilgisayardan Yükle</p>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {uploading && (
              <p className="text-xs text-muted-foreground">Görsel yükleniyor...</p>
            )}
          </div>
          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-xs font-medium text-muted-foreground">URL ile Ekle</p>
            <Input
              placeholder="https://..."
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addImage()}
            />
            <Button size="sm" onClick={addImage}>Ekle</Button>
          </div>
        </PopoverContent>
      </Popover>

      <div className="w-px h-6 bg-border mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        title="Geri Al"
      >
        <Undo className="h-4 w-4" />
      </MenuButton>
      
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        title="Yinele"
      >
        <Redo className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

export const RichTextEditor = ({ content, onChange, placeholder = 'Yazmaya başla...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-accent underline underline-offset-4',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-sm my-4',
          loading: 'lazy',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Underline,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border border-border rounded-sm overflow-hidden bg-background">
      <MenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none
          prose-headings:font-serif prose-headings:text-foreground
          prose-p:text-foreground prose-p:leading-relaxed
          prose-a:text-accent prose-a:no-underline hover:prose-a:underline
          prose-blockquote:border-l-accent prose-blockquote:text-muted-foreground prose-blockquote:italic
          prose-code:bg-muted prose-code:px-1 prose-code:rounded
          prose-img:rounded-sm
          [&_.ProseMirror]:min-h-[250px] [&_.ProseMirror]:outline-none
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none
        "
      />
    </div>
  );
};

export default RichTextEditor;
